import { useCallback, useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { pptPatientService } from '../services/pptPatientService'
import type { PptPatient, PptPatientsImportResponse, PptPatientsImportResult } from '../types/pptPatient'
import './SettingsPptPatientData.css'

const ACCEPTED_FILE_TYPES = '.csv,text/csv'

function formatDobForDisplay(value?: string): string {
  const raw = String(value || '').trim()
  if (!raw) return '—'
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`
  const parsed = new Date(raw)
  if (Number.isNaN(parsed.getTime())) return raw
  const day = String(parsed.getDate()).padStart(2, '0')
  const month = String(parsed.getMonth() + 1).padStart(2, '0')
  const year = parsed.getFullYear()
  return `${day}/${month}/${year}`
}

export function SettingsPptPatientData() {
  const queryClient = useQueryClient()
  const [file, setFile] = useState<File | null>(null)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [activeImportJobId, setActiveImportJobId] = useState<string | null>(null)
  const [lastImportSummary, setLastImportSummary] = useState<PptPatientsImportResponse | null>(null)
  const [editForm, setEditForm] = useState({
    displayName: '',
    dateOfBirth: '',
    gmsOrDvcNumber: '',
    ppsn: '',
  })

  const { data, isLoading } = useQuery({
    queryKey: ['ppt-patients', search, page, pageSize],
    queryFn: () => pptPatientService.list({ page, pageSize, search: search.trim() || undefined }),
  })

  const importMutation = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('Please choose a CSV file first')
      return pptPatientService.importCsv(file)
    },
    onSuccess: (result: PptPatientsImportResult) => {
      setFile(null)
      setPage(1)
      if (result.mode === 'async') {
        setActiveImportJobId(result.jobId)
      } else {
        setLastImportSummary({
          mode: 'sync',
          processed: result.processed,
          inserted: result.inserted,
          duplicatesSkipped: result.duplicatesSkipped,
          invalidRows: result.invalidRows,
          errors: result.errors,
          importBatchId: result.importBatchId,
        })
        queryClient.invalidateQueries({ queryKey: ['ppt-patients'] })
      }
    },
  })

  const importJobQuery = useQuery({
    queryKey: ['ppt-patient-import-job', activeImportJobId],
    queryFn: () => pptPatientService.getImportJob(activeImportJobId as string),
    enabled: !!activeImportJobId,
    refetchInterval: (query) => {
      const status = query.state.data?.status
      return status === 'pending' || status === 'processing' ? 1500 : false
    },
  })

  const asyncImportCompleted = !!importJobQuery.data && (
    importJobQuery.data.status === 'completed' ||
    importJobQuery.data.status === 'completed_with_errors' ||
    importJobQuery.data.status === 'failed'
  )

  useEffect(() => {
    if (!asyncImportCompleted || !activeImportJobId || !importJobQuery.data) return
    setLastImportSummary({
      mode: 'sync',
      processed: importJobQuery.data.processed,
      inserted: importJobQuery.data.inserted,
      duplicatesSkipped: importJobQuery.data.duplicatesSkipped,
      invalidRows: importJobQuery.data.invalidRows,
      errors: importJobQuery.data.errorsSample || [],
      importBatchId: importJobQuery.data.importBatchId,
    })
    setActiveImportJobId(null)
    queryClient.invalidateQueries({ queryKey: ['ppt-patients'] })
  }, [activeImportJobId, asyncImportCompleted, importJobQuery.data, queryClient])

  const updateMutation = useMutation({
    mutationFn: async (payload: { id: string; data: { displayName: string; dateOfBirth?: string; gmsOrDvcNumber?: string; ppsn?: string } }) =>
      pptPatientService.update(payload.id, payload.data),
    onSuccess: () => {
      setEditingId(null)
      queryClient.invalidateQueries({ queryKey: ['ppt-patients'] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => pptPatientService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ppt-patients'] })
    },
  })

  const rows = data?.items ?? []
  const summary = lastImportSummary
  const activeImportJob = importJobQuery.data
  const isImportInProgress = importMutation.isPending || (
    !!activeImportJob &&
    (activeImportJob.status === 'pending' || activeImportJob.status === 'processing')
  )
  const hasErrors = (summary?.errors?.length || 0) > 0
  const visibleErrors = useMemo(() => summary?.errors?.slice(0, 10) ?? [], [summary?.errors])

  const handleDownloadTemplate = useCallback(() => {
    const headers = ['Name', 'DateOfBirth', 'GMSOrDVCNumber', 'PPSN']
    const sampleRow = ['"Barry, Jason William"', '1980-01-31', '1234567A', '1234567T']
    const csv = `${headers.join(',')}\n${sampleRow.join(',')}\n`
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'ppt-patients-template.csv'
    link.click()
    URL.revokeObjectURL(url)
  }, [])

  const startEdit = useCallback((patient: PptPatient) => {
    setEditingId(patient.id)
    setEditForm({
      displayName: patient.displayName || '',
      dateOfBirth: patient.dateOfBirth || '',
      gmsOrDvcNumber: patient.gmsOrDvcNumber || '',
      ppsn: patient.ppsn || '',
    })
  }, [])

  const cancelEdit = useCallback(() => {
    setEditingId(null)
  }, [])

  const saveEdit = useCallback(async () => {
    if (!editingId) return
    await updateMutation.mutateAsync({
      id: editingId,
      data: {
        displayName: editForm.displayName,
        dateOfBirth: editForm.dateOfBirth || undefined,
        gmsOrDvcNumber: editForm.gmsOrDvcNumber || undefined,
        ppsn: editForm.ppsn || undefined,
      },
    })
  }, [editForm.dateOfBirth, editForm.displayName, editForm.gmsOrDvcNumber, editForm.ppsn, editingId, updateMutation])

  return (
    <div className="settings-ppt-patient-data">
      <header className="settings-ppt-patient-data-header">
        <h2>PPT Patients Data</h2>
        <p className="settings-ppt-patient-data-subtitle">
          Upload a CSV to seed the PPT patients table. Duplicate records are skipped automatically.
        </p>
      </header>

      <section className="settings-ppt-patient-data-section" aria-labelledby="upload-heading">
        <h3 id="upload-heading">CSV upload</h3>
        <div className="settings-ppt-patient-data-upload-panel">
          <p className="form-hint">
            Required headers (strict): <code>Name</code>, <code>DateOfBirth</code>, <code>GMSOrDVCNumber</code>, <code>PPSN</code>.
            Name format expected: <code>lastname, firstname middlename</code> and it is stored as <code>firstname middlename lastname</code>.
            If <code>Name</code> contains a comma, wrap it in double quotes (for example: <code>&quot;Barry, Jason William&quot;</code>).
          </p>
          <div className="settings-ppt-patient-data-upload-row">
            <input
              className="settings-ppt-patient-data-file-input"
              type="file"
              accept={ACCEPTED_FILE_TYPES}
              onChange={(event) => setFile(event.target.files?.[0] || null)}
              aria-label="Choose CSV file"
            />
          </div>
          <div className="settings-ppt-patient-data-upload-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={handleDownloadTemplate}
            >
              Download CSV template
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => importMutation.mutate()}
              disabled={!file || isImportInProgress}
            >
              {isImportInProgress ? 'Importing…' : 'Upload and import CSV'}
            </button>
          </div>
          <p className="settings-ppt-patient-data-selected-file">
            {file ? `Selected file: ${file.name}` : 'No file selected'}
          </p>
        </div>
      </section>

      {activeImportJob && (
        <section className="settings-ppt-patient-data-section" aria-labelledby="async-import-heading">
          <h3 id="async-import-heading">Import in progress</h3>
          <div className="settings-ppt-patient-data-summary-grid">
            <div><span>Status</span><strong>{activeImportJob.status}</strong></div>
            <div><span>Total rows</span><strong>{activeImportJob.totalRows}</strong></div>
            <div><span>Processed</span><strong>{activeImportJob.processed}</strong></div>
            <div><span>Inserted</span><strong>{activeImportJob.inserted}</strong></div>
          </div>
          {(activeImportJob.status === 'failed' && activeImportJob.errorMessage) && (
            <p className="form-error" role="alert">{activeImportJob.errorMessage}</p>
          )}
        </section>
      )}

      {summary && (
        <section className="settings-ppt-patient-data-section" aria-labelledby="summary-heading">
          <h3 id="summary-heading">Last import result</h3>
          <div className="settings-ppt-patient-data-summary-grid">
            <div><span>Processed</span><strong>{summary.processed}</strong></div>
            <div><span>Inserted</span><strong>{summary.inserted}</strong></div>
            <div><span>Duplicates skipped</span><strong>{summary.duplicatesSkipped}</strong></div>
            <div><span>Invalid rows</span><strong>{summary.invalidRows}</strong></div>
          </div>
          {hasErrors && (
            <div className="settings-ppt-patient-data-errors" role="alert">
              <strong>Sample row errors</strong>
              <ul>
                {visibleErrors.map((error, index) => (
                  <li key={`${error.row}-${index}`}>Row {error.row}: {error.message}</li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}

      {importMutation.isError && (
        <p className="form-error" role="alert">
          {(importMutation.error as Error).message || 'Failed to import CSV'}
        </p>
      )}

      <section className="settings-ppt-patient-data-section" aria-labelledby="seeded-patients-heading">
        <div className="settings-ppt-patient-data-list-header">
          <h3 id="seeded-patients-heading">Seeded PPT patients</h3>
          <input
            type="search"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value)
              setPage(1)
            }}
            placeholder="Search name, GMS/DVC or PPSN"
            aria-label="Search seeded patients"
          />
        </div>
        {isLoading ? (
          <p>Loading patients…</p>
        ) : rows.length === 0 ? (
          <p>No seeded patients found.</p>
        ) : (
          <div className="settings-ppt-patient-data-table-wrap">
            <table className="settings-ppt-patient-data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Date of birth</th>
                  <th>GMS/DVC</th>
                  <th>PPSN</th>
                  <th>Imported</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((patient) => (
                  <tr key={patient.id}>
                    <td>
                      {editingId === patient.id ? (
                        <input
                          type="text"
                          autoFocus
                          value={editForm.displayName}
                          onChange={(event) => setEditForm((prev) => ({ ...prev, displayName: event.target.value }))}
                          aria-label="Edit patient name"
                        />
                      ) : patient.displayName}
                    </td>
                    <td>
                      {editingId === patient.id ? (
                        <input
                          type="date"
                          value={editForm.dateOfBirth}
                          onChange={(event) => setEditForm((prev) => ({ ...prev, dateOfBirth: event.target.value }))}
                          aria-label="Edit date of birth"
                        />
                      ) : formatDobForDisplay(patient.dateOfBirth)}
                    </td>
                    <td>
                      {editingId === patient.id ? (
                        <input
                          type="text"
                          value={editForm.gmsOrDvcNumber}
                          onChange={(event) => setEditForm((prev) => ({ ...prev, gmsOrDvcNumber: event.target.value }))}
                          aria-label="Edit GMS or DVC number"
                        />
                      ) : (patient.gmsOrDvcNumber || '—')}
                    </td>
                    <td>
                      {editingId === patient.id ? (
                        <input
                          type="text"
                          value={editForm.ppsn}
                          onChange={(event) => setEditForm((prev) => ({ ...prev, ppsn: event.target.value }))}
                          aria-label="Edit PPSN"
                        />
                      ) : (patient.ppsn || '—')}
                    </td>
                    <td>{patient.createdAt ? new Date(patient.createdAt).toLocaleString() : '—'}</td>
                    <td className="settings-ppt-patient-data-actions-cell">
                      {editingId === patient.id ? (
                        <>
                          <button
                            type="button"
                            className="icon-btn icon-btn-save"
                            onClick={() => void saveEdit()}
                            disabled={updateMutation.isPending}
                            aria-label="Save patient"
                            title="Save"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                              <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            className="icon-btn icon-btn-cancel"
                            onClick={cancelEdit}
                            disabled={updateMutation.isPending}
                            aria-label="Cancel editing patient"
                            title="Cancel"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                            </svg>
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="icon-btn icon-btn-edit"
                            onClick={() => startEdit(patient)}
                            disabled={deleteMutation.isPending || updateMutation.isPending}
                            aria-label={`Edit ${patient.displayName}`}
                            title="Edit"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                              <path d="M12 20h9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                              <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            className="icon-btn icon-btn-delete"
                            onClick={() => {
                              if (!window.confirm(`Delete ${patient.displayName}?`)) return
                              void deleteMutation.mutateAsync(patient.id)
                            }}
                            disabled={deleteMutation.isPending || updateMutation.isPending}
                            aria-label={`Delete ${patient.displayName}`}
                            title="Delete"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                              <path d="M3 6h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                              <path d="M8 6V4h8v2M7 6l1 14h8l1-14" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                              <path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!!data && (
          <div className="settings-ppt-patient-data-pagination">
            <div className="settings-ppt-patient-data-pagination-meta">
              <span>
                Showing {rows.length === 0 ? 0 : (data.page - 1) * data.pageSize + 1}
                {' - '}
                {Math.min(data.page * data.pageSize, data.totalCount)} of {data.totalCount}
              </span>
              <label>
                Rows
                <select
                  value={pageSize}
                  onChange={(event) => {
                    setPageSize(Number(event.target.value))
                    setPage(1)
                  }}
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </label>
            </div>
            <div className="settings-ppt-patient-data-pagination-controls">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={data.page <= 1}
              >
                Previous
              </button>
              <span>Page {data.page} of {data.totalPages}</span>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setPage((prev) => Math.min(data.totalPages, prev + 1))}
                disabled={data.page >= data.totalPages}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  )
}
